import { supabase } from '../lib/supabase';
import { RecordingResult } from './recording/types';
import { AudioMixerService } from './recording/AudioMixerService';
import { DatabaseService } from './recording/DatabaseService';
import { DownloadService } from './recording/DownloadService';
import { StorageService } from './recording/StorageService';
import { RecordingProcessor } from './recording/RecordingProcessor';

export interface Recording {
  id: string;
  user_id: string;
  song_id: string;
  title: string;
  recording_url: string;
  recording_type: 'audio' | 'video';
  duration?: number;
  created_at: string;
  updated_at?: string;
  vocals_only_url?: string | null;
  shared_url?: string | null;
  shared?: boolean;
  shared_at?: string | null;
  mixed_recording_url?: string | null;
  final_video_url?: string | null;
  processing_status?: string | null;
  preprocessing_status?: string | null;
  mix_status?: string | null;
  video_processing_status?: string | null;
  audio_sync_offset?: number | null;
  leaderboard_status?: 'submitted' | 'unsubmitted' | 'removed';
  tournament_id?: string | null;
  is_tournament_submission?: boolean;
  submitted_at?: string | null;
  song?: {
    title: string;
    artist: string;
    audio_url?: string;
    cover_url?: string;
    lead_vocal_url?: string;
  };
}

export interface RecordingScoreInfo {
  recordingId: string;
  score: number | null;
  scoringStatus: string | null;
  scoringAttempts: number | null;
  scoringLastAttemptAt: string | null;
}

/**
 * Main service for recording management
 */
export class RecordingService {
  private dbService = new DatabaseService();
  private storageService = new StorageService();
  private downloadService = new DownloadService();
  private recordingProcessor = new RecordingProcessor();

  /**
   * Get all recordings for the current user
   */
  async getUserRecordings(userId?: string): Promise<Recording[]> {
    return this.dbService.getUserRecordings(userId);
  }

  /**
   * Fetch score + scoring lifecycle status for a set of recordings.
   * Scoring state lives on the performances table, not recordings.
   */
  async getRecordingScoreInfo(recordingIds: string[]): Promise<RecordingScoreInfo[]> {
    if (recordingIds.length === 0) return [];

    const { data, error } = await supabase
      .from('performances')
      .select('recording_id, scoring_status, scoring_attempts, scoring_last_attempt_at, performance_analysis (overall_score)')
      .in('recording_id', recordingIds);

    if (error) throw error;

    return (data || []).map((p: any) => ({
      recordingId: p.recording_id,
      score: p.performance_analysis?.[0]?.overall_score ?? null,
      scoringStatus: p.scoring_status ?? null,
      scoringAttempts: p.scoring_attempts ?? null,
      scoringLastAttemptAt: p.scoring_last_attempt_at ?? null,
    }));
  }

  async userHasRecordings(userId: string): Promise<boolean> {
    return this.dbService.userHasRecordings(userId);
  }

  /**
   * Get a shared recording by ID
   */
  async getSharedRecording(id: string): Promise<Recording | null> {
    return this.dbService.getSharedRecording(id);
  }

  /**
   * Share a recording
   */
  async shareRecording(recording: Recording): Promise<string> {
    return this.dbService.shareRecording(recording.id);
  }

  /**
   * Update a recording's title
   */
  async updateRecordingTitle(id: string, title: string): Promise<void> {
    return this.dbService.updateRecordingTitle(id, title);
  }

  /**
   * Delete a recording
   */
  async deleteRecording(id: string): Promise<void> {
    try {
      const { data: recording, error: fetchError } = await supabase
        .from('recordings')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw fetchError;
      }
      if (!recording) {
        throw new Error('Recording not found');
      }

      // Delete the file from storage
      await this.storageService.deleteRecordingFile(recording.recording_url);

      // Check for and delete vocals-only recording if it exists
      if (recording.vocals_only_url) {
        await this.storageService.deleteRecordingFile(recording.vocals_only_url);
      }
      
      // Check for and delete mixed recording if it exists
      if (recording.mixed_recording_url) {
        await this.storageService.deleteRecordingFile(recording.mixed_recording_url);
      }

      // Check for and delete final video if it exists
      if (recording.final_video_url) {
        await this.storageService.deleteRecordingFile(recording.final_video_url);
      }

      // Delete the database record
      await this.dbService.deleteRecordingFromDB(id);
    } catch (error) {
      console.error('Delete recording failed:', error);
      throw error;
    }
  }

  /**
   * Download a recording mixed with backing track
   */
  async downloadRecordingWithBackingTrack(recording: Recording): Promise<void> {
    try {
      if (recording.recording_type === 'video') {
        // For video, show the download options modal
        await this.downloadService.createVideoDownloadModal(recording);
      } else {
        // For audio, mix and download directly
        await this.downloadService.downloadMixedAudio(recording);
      }
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  /**
   * Stop recording and save the result
   */
  async stopAndSaveRecording(
    blob: Blob,
    songId: string,
    mode: 'audio' | 'video',
    duration: number,
    backingTrackUrl: string | null,
    tournamentId?: string | null,
    tournamentRound?: number | null
  ): Promise<RecordingResult> {
    return this.recordingProcessor.stopAndSaveRecording(
      blob,
      songId,
      mode,
      duration,
      backingTrackUrl,
      tournamentId,
      tournamentRound
    );
  }

  /**
   * Get tournament entries for a specific user and tournament
   */
  async getTournamentEntries(tournamentId: string, userId?: string): Promise<Recording[]> {
    try {
      let query = supabase
        .from('recordings')
        .select(`
          *,
          song:songs(title, artist, audio_url, cover_url, lead_vocal_url)
        `)
        .eq('tournament_id', tournamentId)
        .order('created_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching tournament entries:', error);
      throw error;
    }
  }

  /**
   * Submit a tournament entry (mark it as the official submission)
   */
  async submitTournamentEntry(recordingId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the recording to verify it belongs to the user and get tournament_id
      const { data: recording, error: fetchError } = await supabase
        .from('recordings')
        .select('tournament_id, user_id')
        .eq('id', recordingId)
        .single();

      if (fetchError) throw fetchError;
      if (!recording) throw new Error('Recording not found');
      if (recording.user_id !== user.id) throw new Error('Unauthorized');
      if (!recording.tournament_id) throw new Error('This is not a tournament recording');

      // Unsubmit any previous submissions for this tournament
      await supabase
        .from('recordings')
        .update({
          is_tournament_submission: false,
          submitted_at: null
        })
        .eq('user_id', user.id)
        .eq('tournament_id', recording.tournament_id)
        .eq('is_tournament_submission', true);

      // Submit the new entry
      const { error: updateError } = await supabase
        .from('recordings')
        .update({
          is_tournament_submission: true,
          submitted_at: new Date().toISOString()
        })
        .eq('id', recordingId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error submitting tournament entry:', error);
      throw error;
    }
  }

  /**
   * Get the user's submitted entry for a tournament
   */
  async getSubmittedTournamentEntry(tournamentId: string): Promise<Recording | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('recordings')
        .select(`
          *,
          song:songs(title, artist, audio_url, cover_url, lead_vocal_url)
        `)
        .eq('tournament_id', tournamentId)
        .eq('user_id', user.id)
        .eq('is_tournament_submission', true)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching submitted tournament entry:', error);
      throw error;
    }
  }
}